import _ from 'lodash';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Flex, Box } from 'rebass/styled-components';
import styled from 'styled-components';

import { getNextOpenService } from 'shared/helpers/dispensaries';
import { checkAllAddressFlags } from 'src/state/actions/address';
import { RadioGroup, CheckoutAccordion, CheckoutAccordionSummary, CheckoutAccordionActions } from 'shared/components';
import { DefaultInput } from 'shared/components/text-input-with-label';
import {
  AddressAutocomplete,
  AddressAutocompleteInput,
  AddressAutocompleteOptionsList,
  AddressAutocompletePopover,
  AddressAutocompleteGeolocateButton,
} from 'src/components/address-autocomplete';
import { useCheckout } from 'checkout/context';
import useStores from 'shared/hooks/use-stores';
import { AmplitudeEvents, useAmplitude } from 'shared/hooks/use-amplitude';
import { getComplianceConfig } from 'shared/core/helpers/compliance';
import {
  CheckoutLabelGrouping,
  CheckoutLabel,
  CheckoutBody,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';
import { SvgPickupIcon } from 'assets/pickup-icon';
import SvgDeliveryIcon from 'assets/delivery-icon';
import { useSyncOrderTypeAndAddress } from 'checkout/isolated/hooks';

function OrderType({ enabledV2OrderTypes }, ref) {
  const [open, setOpen] = useState(false);
  const [tempDeliveryOption, setTempDeliveryOption] = useState('');
  const [previousDeliveryOption, setPreviousDeliveryOption] = useState('');
  const [disableSave, setDisableSave] = useState('');

  const amplitude = useAmplitude();
  const Checkout = useCheckout();
  const { Cart, UI, apolloClient } = useStores();

  useSyncOrderTypeAndAddress();

  const { isDelivery, isPickup, isCurbsidePickup, isDriveThruPickup, order, orderOptions } = Cart;
  const { dispensary } = order;

  const shouldToggle = orderOptions.length > 1 || tempDeliveryOption === 'delivery' || deliveryEnabled;

  // Order type enabled
  const inStorePickupEnabled = _.includes(enabledV2OrderTypes, 'inStorePickup');
  const curbsidePickupEnabled = _.includes(enabledV2OrderTypes, 'curbsidePickup');
  const driveThruPickupEnabled = _.includes(enabledV2OrderTypes, 'driveThruPickup');
  const deliveryEnabled = _.includes(enabledV2OrderTypes, 'delivery');

  const showInStorePickupLabel =
    !isCurbsidePickup && !isDriveThruPickup && (curbsidePickupEnabled || driveThruPickupEnabled);

  const pickupOptionsCount = _.compact([inStorePickupEnabled, curbsidePickupEnabled, driveThruPickupEnabled]).length;
  const nextAvailablePickupType = getNextOpenService(dispensary, null, false);

  useEffect(() => {
    // Don't allow customers to use invalid pickup type at checkout
    if (
      pickupOptionsCount &&
      ((Cart.orderType === 'pickup' && !inStorePickupEnabled) ||
        (Cart.orderType === 'curbsidePickup' && !curbsidePickupEnabled) ||
        (Cart.orderType === 'driveThruPickup' && !driveThruPickupEnabled))
    ) {
      onChoiceChange(nextAvailablePickupType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // some states allow unverified addresses so we need to allow unverified address to pass this validation
    const dispensaryState = dispensary.location?.state || '';
    const requiresResidentialAddress = getComplianceConfig(dispensaryState, 'residentialDeliveryOnly');
    if (
      (!!_.trim(UI.currentAddress?.ln1) || UI.tempAddress) &&
      Cart.orderType === 'delivery' &&
      requiresResidentialAddress
    ) {
      if (UI.currentAddress?.residentialStatus === 'VERIFIED') {
        Checkout.removeError({ path: 'address' });
        setDisableSave(false);
      } else if (UI.currentAddress?.residentialStatus === 'UNVERIFIED' && !!_.trim(UI.currentAddress?.ln1)) {
        setDisableSave(true);
        Checkout.addErrors([{ message: 'Please enter a valid residential address', path: 'address' }]);
        setOpen(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [UI.currentAddress?.residentialStatus, Cart.orderType, tempDeliveryOption]);

  useEffect(() => {
    if (UI.hasDeliveryAddress(UI.address)) {
      UI.tempAddress = toJS(UI.address);
      UI.tempLocation = toJS(UI.currentAddress);
      UI.apt = UI.currentAddress.ln2;
    } else {
      UI.tempAddress = '';
      UI.tempLocation = '';
      UI.apt = '';
      if (isDelivery) {
        setOpen(true);
      }
    }
    if (!Cart.orderType) {
      setOpen(true);
    } else if (
      Cart.orderType === 'delivery' &&
      UI.hasDeliveryAddress() &&
      !Checkout.getError({ path: 'address' }).error
    ) {
      setOpen(false);
    } else if (isPickup && pickupOptionsCount > 1) {
      setOpen(true);
    }
    setTempDeliveryOption(order.deliveryOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.deliveryOption]);

  // just opened, store current value
  useEffect(() => {
    if (open) {
      setPreviousDeliveryOption(order.deliveryOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // run by onAddressSelected
  async function onAddressCardSelect({ location }) {
    UI.resetAddress();
    const checkResult = await checkAllAddressFlags(UI, Cart, apolloClient, {
      location,
      alert: false,
      getMessage: true,
    });

    return { success: checkResult.success, msg: checkResult.msg };
  }

  function buildAddressFromMapBoxPlace(place) {
    if (!place || !place.lat || !place.lng) {
      return null;
    }

    return {
      tempAddress: place.addressLong,
      tempLocation: {
        ...place,
        lat: place.lat,
        lng: place.lng,
      },
    };
  }

  // run when you click an address in the suggestions dropdown
  async function onAddressSelected(place) {
    const validPlace = buildAddressFromMapBoxPlace(place);

    if (!validPlace) {
      return;
    }
    setDisableSave(true);

    UI.tempAddress = validPlace.tempAddress;
    UI.apt = '';
    UI.tempLocation = validPlace.tempLocation;

    const result = await onAddressCardSelect({ location: toJS(UI.tempLocation) });

    // result.success = false, opens residential verification modal
    if (result.success) {
      setDisableSave(false);
    }
    // Messages for no address or when address is out of delivery bounds
    if (result.msg && _.isString(result.msg)) {
      Checkout.addErrors([{ message: result.msg, path: 'address' }]);
    }
  }

  function onChoiceChange(value) {
    // update global value
    setTempDeliveryOption(value);
    Checkout.removeError({ path: 'orderType' });
    Cart.setOrderType(value);
    Cart.clearReservation();
    Cart.removeTip();
  }

  async function onAddressCardSave({ location }) {
    const checkResult = await checkAllAddressFlags(UI, Cart, apolloClient, {
      location,
      alert: false,
      getMessage: true,
    });

    if (checkResult.success) {
      UI.updateAddress({ location, residentialStatus: 'VERIFIED' });
      Cart.updateAddress({ location, residentialStatus: 'VERIFIED' });

      Cart.setReservation(null);

      return { success: true };
    }

    return { success: false, msg: checkResult.msg };
  }

  async function saveAddress() {
    const location = { ...UI.tempLocation, ln2: UI.apt };
    const result = await onAddressCardSave({ location });

    if (!result.success) {
      Checkout.addErrors([{ message: result.msg, path: 'address' }]);
      return false;
    }
    return true;
  }

  // when save button is clicked and the thing collapses
  async function onSave() {
    amplitude.log(AmplitudeEvents.checkout.clickSave, {
      componentName: `Order Type`,
      dispensaryId: dispensary?.id,
      dispensaryName: dispensary?.name,
    });

    if (!tempDeliveryOption) {
      Checkout.addErrors([{ message: 'Please select an order type', path: 'orderType' }]);
      throw new Error(`onSave error: 'Please select an order type'`);
    }

    if (
      (!UI.tempAddress || UI.tempAddress === '') &&
      !_.includes(['pickup', 'curbsidePickup', 'driveThruPickup'], tempDeliveryOption)
    ) {
      // eslint-disable-next-line max-len
      // this onSave() is used as a validation in on-click-submit.js. We need to throw errors here so `await` will catch it and prevent submission
      Checkout.addErrors([{ message: 'Please enter an address', path: 'address' }]);
      throw new Error(`onSave error: 'Please enter an address'`);
    } else if (disableSave && UI.currentAddress?.residentialStatus === 'UNVERIFIED') {
      Checkout.addErrors([{ message: 'Please enter a valid residential address', path: 'address' }]);
      throw new Error(`onSave error: 'Please enter a valid residential address'`);
    } else if (!disableSave) {
      if (tempDeliveryOption === 'delivery') {
        const result = await saveAddress();
        if (result) {
          Cart.setOrderType(tempDeliveryOption);
          if (open) {
            setOpen(false);
          }
        }
      } else {
        Cart.setOrderType(tempDeliveryOption);
        if (open) {
          setOpen(false);
        }
      }
    }
  }

  // onSave is called in validateSubmit.js ln 196.
  useImperativeHandle(ref, () => ({ onSave }));

  function reset() {
    setTempDeliveryOption(previousDeliveryOption);
    Cart.setOrderType(previousDeliveryOption);
    Checkout.removeError({ match: 'address' });
    setOpen(!open);
  }

  return (
    <CheckoutLabelGrouping id='orderType' data-testid='order-type-section'>
      <CheckoutLabel card>
        <CheckoutSectionHeading>Type</CheckoutSectionHeading>
      </CheckoutLabel>

      <CheckoutBody>
        <StyledCheckoutAccordion
          expanded={shouldToggle ? open : false}
          dataCyName='order-type'
          onExpand={shouldToggle ? () => setOpen(!open) : _.noop}
          hideToggles={!shouldToggle}
          onSave={onSave}
          error={Checkout.getError({ card: 'orderType' }).error}
          cursor={shouldToggle ? 'pointer' : 'default'}
          onCancel={reset}
          crossfade
        >
          <CheckoutAccordionSummary>
            <Flex alignItems='center'>
              {isDelivery ? (
                <SvgDeliveryIcon fill='#A3B2C1' height={16} width={26} />
              ) : (
                <SvgPickupIcon height={17} width={15} />
              )}
              <SummaryText data-testid='order-type-label'>
                <strong>
                  {isDelivery && 'Delivery '}
                  {isPickup && 'Pickup '}
                </strong>
                <span>
                  {!isDelivery && !isPickup && 'Please select an order type'}
                  {isPickup && showInStorePickupLabel && '(In-Store)'}
                  {isCurbsidePickup && '(Curbside)'}
                  {isDriveThruPickup && '(Drive-thru)'}
                  {isDelivery && ` to ${UI.formattedAddressForCheckout}`}
                </span>
              </SummaryText>
            </Flex>
          </CheckoutAccordionSummary>
          <CheckoutAccordionActions>
            <RadioGroup
              options={orderOptions}
              hasSublabels
              display='block'
              defaultValue={tempDeliveryOption}
              checked={tempDeliveryOption}
              onChange={onChoiceChange}
              mb='12px'
              name='orderType'
              showChecksWhenDisabled
              required
            />
            {tempDeliveryOption === 'delivery' && (
              <Box mb={16}>
                <AddressAutocomplete onSelect={onAddressSelected} suggestionTypes={['address']}>
                  <PopoverWrapper>
                    <StyledDefaultInput
                      id='autoComplete'
                      label='Delivery Address'
                      mb='12px'
                      error={Checkout.getError({ path: 'address' }).error}
                      errorMessage={Checkout.getError({ path: 'address' }).message}
                      onValueAfterError={() => Checkout.removeError({ path: 'address' })}
                      placeholder='Enter a street address'
                      InputProps={{
                        inputComponent: AddressAutocompleteInput,
                        onPlaceSelected: onAddressSelected,
                      }}
                      data-cy='delivery-address-input'
                      data-test='delivery-address-input'
                      data-testid='delivery-address-input'
                      required
                    />
                    <StyledPopover>
                      <AddressAutocompleteGeolocateButton />
                      <AddressAutocompleteOptionsList />
                    </StyledPopover>
                  </PopoverWrapper>
                </AddressAutocomplete>
                <StyledDefaultInput
                  id='apartment'
                  label='Apartment or suite number'
                  value={UI.apt}
                  onChange={({ target }) => (UI.apt = target.value)}
                  data-cy='apartment-number-input'
                  data-test='apartment-number-input'
                  data-testid='apartment-number-input'
                />
              </Box>
            )}
          </CheckoutAccordionActions>
        </StyledCheckoutAccordion>
      </CheckoutBody>
    </CheckoutLabelGrouping>
  );
}

export default observer(forwardRef(OrderType));

const StyledCheckoutAccordion = styled(CheckoutAccordion)`
  .MuiAccordionSummary-root:hover:not(.Mui-disabled) {
    cursor: ${({ cursor }) => cursor};
  }
`;

const StyledDefaultInput = styled(DefaultInput)`
  width: 100%;
`;

const PopoverWrapper = styled.div`
  position: relative;
`;

const StyledPopover = styled(AddressAutocompletePopover)`
  position: absolute;
  top: calc(100% - 8px);
  left: 0;
  right: 0;
  z-index: 9999;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.2);
  padding: 16px;

  [data-reach-combobox-popover] {
    position: static;
    border: none;
    border-radius: 8px;
    box-shadow: none;
    width: 100%;
    padding: 0;
    margin: 0;
    background: transparent;
  }
`;

const SummaryText = styled.span`
  color: var(--dcm-checkout-text-color, #5d666d);
  font-size: 14px;
  padding-left: 15px;

  strong {
    font-weight: bold;
  }
`;
